import { memo, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@Hooks/index';
import { MaterialRequisitionItemFormResponse } from '@Types/api/form';
import { FormikHelpers, useFormik } from 'formik';
import MaterialEmptyScreen from '../Base/MaterialEmptyScreen/MaterialEmptyScreen';
import MaterialListItems from './components/MaterialListItems/MaterialListItems';
import MaterialModalContent from './components/MaterialModalContent/MaterialModalContent';
import MaterialAddModal from '../Base/MaterialAddModal/MaterialAddModal';

import { materialRequestActions } from '../../../store/materialRequestSlice';
import { MaterialItemRequestFormValues } from './types/types';
import {
  currentMaterialRequest,
  isButtonDisable,
  modalManager,
} from '../../../store/selectors/materialRequestSelector';
import MaterialItemSchema from './validationSchema';
import { initialValuesForm } from './initialValues';

type handleSubmitType = (
  values: MaterialItemRequestFormValues,
  formikHelpers: FormikHelpers<MaterialItemRequestFormValues>
) => void;

interface MaterialsSectionProps {
  listItems: Array<MaterialRequisitionItemFormResponse>;
  handleSaveMaterialItem: (
    formikValues: MaterialRequisitionItemFormResponse,
    requisitionId: number,
    addAnotherItem?: boolean
  ) => void;
}

const MaterialsSection: React.FC<MaterialsSectionProps> = ({
  listItems,
  handleSaveMaterialItem,
}) => {
  const dispatch = useAppDispatch();
  const modalManagerSetting = useAppSelector(modalManager);
  const { open, title, okText, isEditMode } = modalManagerSetting;
  const stateButton = useAppSelector(isButtonDisable);
  const currentMRequest = useAppSelector(currentMaterialRequest);
  const handleSubmitForm: handleSubmitType = (): void => {};

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    onSubmit: handleSubmitForm,
    validationSchema: MaterialItemSchema,
  });

  const handleDisableSaveButton = (isDisable: boolean): void => {
    dispatch(materialRequestActions.setModalButtonDisable(isDisable));
  };

  const clearAll = (): void => {
    dispatch(
      materialRequestActions.setModalManager({
        isEditMode: false,
        okText: '',
        title: '',
        open: false,
      })
    );
    handleDisableSaveButton(true);
    formik.resetForm();
  };

  const handleCloseModal = (): void => {
    clearAll();
  };

  const handleOkModal = useCallback((): void => {
    handleSaveMaterialItem(formik.values, currentMRequest?.requestId);
    clearAll();
  }, [formik.values, currentMRequest?.requestId]);

  const handleEditMaterialItem = (
    materialItem: MaterialRequisitionItemFormResponse
  ): void => {
    formik.setValues({
      ...materialItem,
    });

    dispatch(
      materialRequestActions.setModalManager({
        isEditMode: true,
        okText: 'Save Changes',
        title: 'Edit Material',
        open: true,
      })
    );

    handleDisableSaveButton(false);
  };

  const handleDeleteItem = useCallback(
    (requisitionItem: Array<string | number>) => {
      dispatch(
        materialRequestActions.setModalManagerItem({
          deleteItem: requisitionItem as Array<number>,
          messageModal: `Are you sure you want to delete material no.${requisitionItem[0]}?`,
          titleModal: 'Delete Material',
          modalType: 'Material',
          showModal: true,
        })
      );
    },
    []
  );

  return (
    <>
      {listItems?.length ? (
        <MaterialListItems
          onEditMaterialItem={handleEditMaterialItem}
          onDeleteItem={handleDeleteItem}
          listItems={listItems}
        />
      ) : (
        <MaterialEmptyScreen text="Please Add Materials To Proceed" />
      )}

      <MaterialAddModal
        ModalContent={
          <MaterialModalContent
            isEditMode={isEditMode}
            isDisableSaveButton={stateButton}
            formik={formik}
            onSave={handleSaveMaterialItem}
            onDisableSaveButton={handleDisableSaveButton}
          />
        }
        okText={okText}
        title={title}
        handleCancel={handleCloseModal}
        handleOk={handleOkModal}
        modalOpen={open}
        isDisableSaveButton={stateButton}
      />
    </>
  );
};

export default memo(MaterialsSection);
